import { create } from 'zustand';
import { api } from '../lib/api-client';
import type { DrawStroke } from '@spine-workspace/shared';

export interface TeamMember {
  id: string;
  name: string;
  avatarUrl?: string;
}

interface ReviewState {
  /** Whether the Review tab is active in the right sidebar */
  isOpen: boolean;
  /** Version filter: 'all' or a specific version number */
  activeVersion: number | 'all';
  /** Team members available for @ mention autocomplete */
  teamMembers: TeamMember[];
  teamMembersLoaded: boolean;

  /** Per-version review draw strokes (stored in memory, not persisted) */
  reviewStrokes: Record<number, DrawStroke[]>;
  /** Which version is currently being drawn on (null = not in draw mode) */
  drawingVersion: number | null;
  /** Active draw color for review annotations */
  reviewDrawColor: string;

  open(): void;
  close(): void;
  toggle(): void;
  setActiveVersion(v: number | 'all'): void;
  fetchTeamMembers(sceneId?: string): Promise<void>;

  startReviewDraw(version: number): void;
  stopReviewDraw(): void;
  setReviewDrawColor(color: string): void;
  addReviewStroke(version: number, stroke: DrawStroke): void;
  undoReviewStroke(version: number): void;
  clearReviewStrokes(version: number): void;
}

export const useReviewStore = create<ReviewState>((set) => ({
  isOpen: false,
  activeVersion: 'all',
  teamMembers: [],
  teamMembersLoaded: false,
  reviewStrokes: {},
  drawingVersion: null,
  reviewDrawColor: '#f87171', // red-400

  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  setActiveVersion: (v) => set({ activeVersion: v }),

  startReviewDraw: (version) => set({ drawingVersion: version }),
  stopReviewDraw: () => set({ drawingVersion: null }),
  setReviewDrawColor: (color) => set({ reviewDrawColor: color }),

  addReviewStroke: (version, stroke) =>
    set((s) => ({
      reviewStrokes: {
        ...s.reviewStrokes,
        [version]: [...(s.reviewStrokes[version] ?? []), stroke],
      },
    })),

  undoReviewStroke: (version) =>
    set((s) => ({
      reviewStrokes: {
        ...s.reviewStrokes,
        [version]: (s.reviewStrokes[version] ?? []).slice(0, -1),
      },
    })),

  clearReviewStrokes: (version) =>
    set((s) => ({
      reviewStrokes: { ...s.reviewStrokes, [version]: [] },
    })),

  fetchTeamMembers: async (_sceneId?: string) => {
    try {
      const res = await api.get<{
        users: Array<{ id: string; name: string; avatarUrl?: string }>;
      }>('/api/auth/users');
      set({ teamMembers: res.users, teamMembersLoaded: true });
    } catch {
      set({ teamMembersLoaded: true });
    }
  },
}));
